// ---------------------------------------------------------------------------
// Wire protocol: client intents → server, authoritative ops → clients.
// JSON over WebSocket. High-frequency position updates use compact arrays.
// ---------------------------------------------------------------------------

import type { ObjectDef } from "./defs";
import type { DefsServerMsg, ImportDeckIntent } from "./dynamicDefs";
import type { GameObject, Player } from "./state";

// --------------------------- client → server -------------------------------

export type Intent =
  /** Grab an object. `split` pulls the top item off a container instead. */
  | { t: "pickup"; id: string; split?: boolean }
  /** Move a held object (throttled ~30 Hz). x/z on table plane. */
  | { t: "move"; id: string; x: number; z: number; yaw: number }
  /** Batched move of several held objects (multi-select drag). */
  | { t: "moveMany"; o: Array<[string, number, number, number]> } // [id, x, z, yaw]
  /** Drop a held object at a point; server resolves merge/hand/settle. */
  | { t: "release"; id: string; x: number; z: number }
  /**
   * Drop a dragged group. (tx,tz) is the drop point of the card under the
   * cursor: if a merge target sits there, ALL compatible items join it;
   * otherwise items settle at their individual positions.
   */
  | { t: "releaseMany"; o: Array<[string, number, number]>; tx: number; tz: number }
  /** Ask to see a container's contents (announced to the room). */
  | { t: "inspect"; id: string }
  /** Panel closed — stop pushing contents refreshes. */
  | { t: "inspectClose"; id: string }
  /** Drop a held object INTO a container (e.g. onto the open deck view). */
  | { t: "putInto"; id: string; container: string }
  /** Take/move one item out of a container by index (from an inspect panel).
   *  dest: hand (default) | table (beside it) | top/bottom (reorder) |
   *  drag (materialize held by the sender — they drag it out). */
  | { t: "takeAt"; id: string; index: number; dest?: "hand" | "table" | "top" | "bottom" | "drag" }
  | { t: "flip"; id: string }
  | { t: "rotate"; id: string; yaw: number }
  | { t: "shuffle"; id: string }
  /** Draw n cards from a container to the sender's hand zone. */
  | { t: "draw"; id: string; n: number }
  /** Deal n cards from a container to every connected player's hand zone,
   *  round-robin like a real deal (starting left of the dealer). */
  | { t: "deal"; id: string; n: number }
  /** Sort the cards in the sender's hand zone alphabetically. */
  | { t: "sortHand" }
  /** Table chat: say something to everyone at the table. */
  | { t: "chat"; m: string }
  /** Gather every card on the trick slots into a face-down pile by the
   *  sender's hand zone (trick-taking tables: the winner collects). */
  | { t: "takeTrick" }
  /** Split a container: take `n` items off the top as a new held container. */
  | { t: "split"; id: string; n: number }
  /** Roll dice (server-side, seeded RNG). Non-dice ids are ignored. */
  | { t: "roll"; ids: string[] }
  | { t: "spawn"; def: string; x: number; z: number; faceUp?: boolean }
  /** Spawn a preset group (full deck, chip set...). */
  | { t: "spawnPreset"; preset: string; x: number; z: number }
  /** Replace the table with a named setup from the shared table registry. */
  | { t: "setTable"; type: string }
  | { t: "delete"; id: string }
  | { t: "lock"; id: string }
  | { t: "cursor"; x: number; z: number }
  /** Serialize objects for the client's bag (server owns container contents). */
  | { t: "saveObjects"; ids: string[]; name: string }
  /** Spawn a previously saved bag entry (validated server-side). */
  | { t: "spawnSaved"; data: unknown; x: number; z: number }
  | { t: "undo" }
  | { t: "redo" }
  // ---- MTG / commander-table intents ----
  /** Move objects to the sender's slot pile (graveyard/exile/inkwell/…). */
  | { t: "toSlot"; ids: string[]; slot: "graveyard" | "exile" | "library" | "command" | "inkwell" }
  /** Top 6 of a deck → the sender's prize slot, face-down (Pokémon setup). */
  | { t: "prizes"; id: string }
  /** Move cards/piles to the sender's hand zone (bounce). */
  | { t: "toHand"; ids: string[] }
  /** Shuffle the sender's hand back into their library pile, then draw n. */
  | { t: "mulligan"; n: number }
  /** Top n of a library → the sender's graveyard pile. */
  | { t: "mill"; id: string; n: number }
  /** Look at top n (requester only); resolve puts some back / away. */
  | { t: "scry"; id: string; n: number; mode: "scry" | "surveil" }
  /**
   * keepTop: indices (into the viewed top-first list) staying on top, in the
   * new top-first order; the rest go to the bottom (scry) / graveyard (surveil).
   */
  | { t: "scryResolve"; id: string; mode: "scry" | "surveil"; keepTop: number[]; away: number[] }
  /** Show the top n of a container to the whole room. */
  | { t: "reveal"; id: string; n: number }
  /** Untap your cards on your mat + draw 1. */
  | { t: "newTurn" }
  /** Adjust an MTG counter on a card (delta may be negative). */
  | { t: "counter"; id: string; cid: string; label: string; delta: number }
  /** Spawn a colored counter chip (optionally labeled). */
  | { t: "spawnCounter"; color: string; label: string; x: number; z: number }
  | { t: "life"; seat: number; delta: number }
  | { t: "cmdDmg"; from: number; to: number; delta: number }
  | { t: "resetTable" }
  /** Import a deck (MTG/Pokémon/Lorcana; pasted list and/or deck URL) at (x,z). */
  | ImportDeckIntent;

// --------------------------- server → client -------------------------------

/** Public (redacted) view of an object — what actually goes over the wire. */
export type PublicObj = Omit<GameObject, "contents">;

export type ServerMsg =
  | {
      t: "welcome";
      you: PlayerId_;
      seat: number;
      roomId: string;
      players: Player[];
      objs: PublicObj[];
      /** Runtime-registered defs (imported cards) — apply before objs render. */
      defs?: ObjectDef[];
      /** Table dimensions + type (renderers size the table from this). */
      table?: { w: number; d: number; type: string };
    }
  | { t: "players"; players: Player[] }
  /** Create or full-update objects (small; sent on any non-positional change). */
  | { t: "upsert"; objs: PublicObj[] }
  | { t: "remove"; ids: string[] }
  /** High-frequency movement batch: [id, x, y, z, yaw][] */
  | { t: "pos"; o: Array<[string, number, number, number, number]> }
  /** Cursor batch: [playerId, x, z][] */
  | { t: "cursors"; c: Array<[string, number, number]> }
  | { t: "log"; m: string }
  /** Table chat line (rendered with the sender's seat color). */
  | { t: "chat"; pid: string; who: string; color: string; m: string }
  /** One-shot visual effects (shuffle riffle, deal, dice tumble). */
  | { t: "fx"; kind: "shuffle" | "deal" | "roll"; id: string }
  | { t: "error"; m: string }
  /** Container contents, sent ONLY to inspecting players (bottom→top). */
  | { t: "inspect"; id: string; items: Array<{ def: string; name?: string }> }
  /** Serialized objects + defs for a bag save (response to saveObjects). */
  | { t: "savedObjects"; name: string; data: { objects: unknown[]; defs: ObjectDef[] } }
  /** Life totals + commander damage matrix (indexed by seat). */
  | { t: "gameState"; life: number[]; cmd: number[][] }
  /** Requester-only: top n of a container for scry/surveil. Top-first. */
  | { t: "scryView"; id: string; mode: "scry" | "surveil"; items: Array<{ def: string; name?: string }> }
  /** Room-wide: revealed top cards. Top-first. */
  | { t: "revealView"; id: string; who: string; items: Array<{ def: string; name?: string }> }
  /** Dynamic defs were added (deck import) — apply via registerDynamicDefs. */
  | DefsServerMsg;

type PlayerId_ = string;

export const MOVE_HZ = 30;
export const CURSOR_HZ = 15;
